import { useState, useEffect } from "react";
import { MotiView } from "moti";
import { useNavigation } from "@react-navigation/native";
import { View, StyleSheet } from "react-native";
import {
  useGetCoursesByFacultyQuery,
  useGetUserPermissionsQuery,
  useMeQuery,
} from "../../../graphql/generated/graphql";
import Skeleton from "react-loading-skeleton";
import ClassCardProgressList from "../../../components/lms/ClassCardProgressList/ClassCardProgressList";
import { RolePermissions } from "../../../constants/rolesAndPermissions";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { shadowStyles } from "../../../styles/shadow";
import usePagination from "../../../hooks/usePagination";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import TableControlsContainer from "../../general/TableControls/TableControlsContainer";
import Pagination from "../../../components/common/TableControls/Pagination/Pagination";
import { RootStackParamList } from "../../../navigation/AppNavigator.web";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";

const ITEMS_PER_PAGE = 50;

const ClassCardProgressListContainer = ({ integrationId }) => {
  const [currentCourses, setCurrentCourses] = useState([]);
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { data: userPermissionsData } = useGetUserPermissionsQuery();
  const { data: meData } = useMeQuery({});

  const rolePermissions = userPermissionsData?.getUserPermissions ?? [];

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: ITEMS_PER_PAGE });

  const {
    data: courses,
    loading: coursesLoading,
    refetch: refetchCourses,
  } = useGetCoursesByFacultyQuery({
    variables: {
      userId: meData?.me?.id,
      query: "",
    },
    skip: !meData?.me?.id,
  });

  useEffect(() => {
    if (courses) {
      setCurrentCourses(courses.getCoursesByFaculty?.data);
    }
  }, [courses]);

  const totalItemsCount = courses?.getCoursesByFaculty?.count || 0;

  function formatCoursesData(courses) {
    return (courses ?? []).map((course) => {
      const disabled = rolePermissions.includes(RolePermissions.courseEdit)
        ? false
        : course.startDate && new Date(course.startDate) > new Date();
      const isTeachingThisCourse = course?.teachingFaculty?.some((teacher) => teacher.id === meData?.me?.id);
      const isAdmin = userPermissionsData?.getUserPermissions.includes(RolePermissions.analyticsViewSchool);
      const hasEdit = (isAdmin || isTeachingThisCourse) && rolePermissions.includes(RolePermissions.courseEdit);
      return {
        ...course,
        disabled,
        hasEdit,
      };
    });
  }

  // Polling until all courses are generated or failed
  useEffect(() => {
    let pollingInterval;

    const checkGenerationStatus = () => {
      const hasGeneratingCourses = currentCourses.some(
        (course) => course.generationStatus !== "completed" && course.generationStatus !== "failed"
      );

      if (hasGeneratingCourses) {
        refetchCourses();
      } else if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };

    if (currentCourses.length > 0) {
      // Poll every 10 seconds if there are courses being generated
      pollingInterval = setInterval(checkGenerationStatus, 10000);
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [currentCourses, refetchCourses]);

  const handlePress = (id) => {
    navigation.navigate("Custom Course Student", {
      course_id: id,
      integration_id: integrationId,
    });
  };

  const handleEditPress = (id) => {
    navigation.navigate("Custom Coursework Modules", {
      courseId: id,
    });
  };

  const handleAnalyticsPress = (id) => {
    navigation.navigate("Analytics", {
      courseId: id,
    });
  };

  if (coursesLoading) {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        {[...Array(3)].map((_, index) => (
          <Skeleton height={210} borderRadius={30} key={index} />
        ))}
      </View>
    );
  }

  // Custom placeholder for no courses when enrollment is possible
  const NoCoursesPlaceholder = () => (
    <View style={[styles.container, shadowStyles.primary]}>
      <View style={styles.content}>
        <View style={styles.textContainer}>
          <CustomText text="No Assigned Courses" size="xs" textType="display" style={styles.title} />
          <CustomText text="School Administrators will assign courses to you." size="xs" style={styles.subtitle} />
        </View>
      </View>
    </View>
  );

  return (
    <MotiView
      from={{ opacity: 0, translateY: 20 }}
      animate={{ opacity: 1, translateY: 0 }}
      delay={500}
      // @ts-expect-error - transition is not typed
      transition={{ type: "timing", duration: 300 }}
      style={{ flex: 1 }}
      aria-live="polite">
      {totalItemsCount > ITEMS_PER_PAGE && (
        <Pagination page={page} handlePageChange={setPage} totalPageCount={Math.ceil(totalItemsCount / pageSize)} />
      )}
      <CustomSpacing type="vertical" size="m" />
      {currentCourses && currentCourses.length > 0 ? (
        <ClassCardProgressList
          courses={formatCoursesData(currentCourses)}
          onPress={handlePress}
          hasAnalytics={rolePermissions.includes(RolePermissions.analyticsViewFaculty)}
          onEditPress={handleEditPress}
          onAnalyticsPress={handleAnalyticsPress}
        />
      ) : (
        <NoCoursesPlaceholder />
      )}
      <CustomSpacing type="vertical" size="m" />
      {totalItemsCount > ITEMS_PER_PAGE && (
        <TableControlsContainer
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={totalItemsCount}
        />
      )}
    </MotiView>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 20,
    marginTop: 40,
    marginBottom: 20,
    marginHorizontal: 10,
    padding: 36,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
  },
  content: {
    alignItems: "center",
    justifyContent: "center",
  },
  textContainer: {
    alignItems: "center",
    justifyContent: "flex-start",
  },
  title: {
    textAlign: "center",
    marginBottom: 8,
  },
  subtitle: {
    textAlign: "center",
    maxWidth: 280,
    marginBottom: 24,
    color: "#667085",
  },
});

export default ClassCardProgressListContainer;
